import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { DsButtonVariants, Icons } from '@levelaccess/design-system';

import { chromeBrowserFlags, defaultChromeBrowserFlags } from '../../../../shared/constants/chrome-browser-flags';
import { CustomValidators } from '../../services/helpers/form-custom-validators';
import { validationError } from '../../constants/form.constants';

enum $fields {
  browserFlags = 'browserFlags',
  modifyBrowserFlags = 'modifyBrowserFlags',
  newBrowserFlag = 'newBrowserFlag',
  newBrowserFlagForm = 'newBrowserFlagForm',
}

const MAX_BROWSER_FLAGS_LENGTH: number = 140;

@Component({
  selector: 'app-scan-custom-browser-flags',
  templateUrl: './scan-custom-browser-flags.component.html',
  styleUrls: ['./scan-custom-browser-flags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScanCustomBrowserFlagsComponent {
  @Input()
  public form: UntypedFormGroup;
  @Input()
  public formValidationRequest$: Subject<void>;
  public browserFlagsControlName: string = $fields.browserFlags;
  public modifyBrowserFlagsControlName: string = $fields.modifyBrowserFlags;
  public newBrowserFlagControlName: string = $fields.newBrowserFlag;
  public newBrowserFlagFormName: string = $fields.newBrowserFlagForm;
  public newBrowserFlagForm: UntypedFormGroup;
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly Icons: typeof Icons = Icons;
  public browserFlagsValidationRequest$: Subject<void> = new Subject<void>();

  constructor(private formBuilder: UntypedFormBuilder) {
    this.newBrowserFlagForm = this.formBuilder.group(
      {
        [this.newBrowserFlagControlName]: this.formBuilder.control('', [
          Validators.minLength(1),
          Validators.maxLength(MAX_BROWSER_FLAGS_LENGTH),
          CustomValidators.validateBrowserFlag,
          CustomValidators.required,
        ]),
      },
      { updateOn: 'submit' },
    );
  }

  @Input()
  public set browserFlags(browserFlags: string[]) {
    if (this.form) {
      const effectiveBrowserFlags = browserFlags?.length ? browserFlags : [...defaultChromeBrowserFlags];
      this.form.registerControl(this.browserFlagsControlName, this.formBuilder.control(effectiveBrowserFlags));
    }
  }

  @Input()
  public set modifyBrowserFlags(modifyBrowserFlags: boolean) {
    if (this.form) {
      this.form.registerControl(this.modifyBrowserFlagsControlName, this.formBuilder.control(Boolean(modifyBrowserFlags)));
    }
  }

  public get modifyBrowserFlags(): AbstractControl | null {
    return this.form.get(this.modifyBrowserFlagsControlName);
  }

  public get browserFlags(): AbstractControl | null {
    return this.form.get(this.browserFlagsControlName);
  }

  public get newBrowserFlag(): AbstractControl | null {
    return this.newBrowserFlagForm.get(this.newBrowserFlagControlName);
  }

  protected addNewBrowserFlag(): void {
    const newBrowserFlagValue = this.newBrowserFlag?.value?.trim() || '';
    this.newBrowserFlagForm.markAllAsTouched();

    if (!this.newBrowserFlag?.valid) {
      this.browserFlagsValidationRequest$.next();
      return;
    }

    if (this.browserFlags?.value.includes(newBrowserFlagValue)) {
      this.newBrowserFlag?.setErrors({ [validationError.duplicateBrowserFlag]: true });
      this.browserFlagsValidationRequest$.next();
      return;
    }

    if (!chromeBrowserFlags.has(newBrowserFlagValue)) {
      this.newBrowserFlag?.setErrors({ [validationError.invalidBrowserFlag]: true });
      this.browserFlagsValidationRequest$.next();
      return;
    }

    this.browserFlags.setValue([...this.browserFlags.value, newBrowserFlagValue]);
    this.newBrowserFlagForm.reset();
  }

  public removeBrowserFlag(flag: string): void {
    const currentFlags = this.browserFlags.value;
    const updatedFlags = currentFlags.filter((f: string) => f !== flag);
    this.browserFlags.setValue(updatedFlags);
    this.form.markAsDirty();
  }

  public onNewBrowserFlagInput(value: string): void {
    this.newBrowserFlagForm.reset({ [this.newBrowserFlagControlName]: value });
  }
}
